<template>
  <div class="flex min-h-screen max-w-[100vw]">
    <!-- Sidebar -->
    <Transition name="slide">
      <SideBar
        v-if="isSidebarVisible"
        class="z-50 md:block"
        :is-sidebar-shrink="isSideBarShrink"
        @sidebarClose="toggleSidebar"
        @linkClicked="toggleSidebar"
      />
    </Transition>

    <!-- Main Content Area -->
    <div
      v-if="!deviceStore.isMobile || !isSidebarVisible"
      class="flex flex-col flex-1 transition-all duration-300 ease-in-out"
      :class="{ 'md:ml-20': isSideBarShrink, 'md:ml-64': !isSideBarShrink }"
    >
      <!-- Header -->
      <header class="flex items-center py-2 z-50 bg-white dark:bg-gray-900 sticky top-0 h-16 shadow-md">
        <!-- Mobile Sidebar Toggle-->
        <Button
          icon="pi pi-align-left text-2xl"
          severity="secondary"
          variant="text"
          rounded
          aria-label="Toggle sidebar"
          class="ml-2 md:hidden"
          size="large"
          @click="toggleSidebar"
        />

        <!-- Sidebar Shrink/Expand Toggle -->
        <Button
          :icon="isSideBarShrink ? 'pi pi-window-maximize' : 'pi pi-window-minimize'"
          severity="secondary"
          variant="text"
          rounded
          aria-label="Filter"
          class="hidden md:inline-flex ml-2"
          size="large"
          @click="sidebarStore.toggleShrink()"
        />

        <!-- Search field -->
        <div v-if="authStore.can('use global search')" class="ml-2 md:ml-4 flex-1 hidden md:block">
          <IconField>
            <InputIcon class="pi pi-search" />
            <InputText
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              variant="filled"
              placeholder="Search EQO, Customers, Suppliers, Stocks, Warehouse, Employee"
              class="w-40 md:w-64 lg:w-96 text-sm"
              autocapitalize="off"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              inputmode="search"
              enterkeyhint="search"
              @focus="openSearchDropdown"
              @click="openSearchDropdown"
              @keydown.esc="closeSearchDropdown"
              @input="debouncedSearch"
            />
          </IconField>

          <Popover ref="searchPanel" append-to="body" class="min-w-[30vw] max-w-[38vw] !z-50">
            <div class="flex flex-col gap-3">
              <div v-if="allowedSearchFilters.length" class="flex gap-2 border-b border-gray-500 pb-3 w-full">
                <Button
                  v-for="f in allowedSearchFilters"
                  :key="f.key"
                  size="small"
                  :severity="selectedFilter === f.key ? 'primary' : 'secondary'"
                  @click="selectFilter(f.key)"
                >
                  {{ f.label }}
                </Button>
              </div>

              <span
                v-if="!searchResults.length && searchQuery.length < MIN_SEARCH_LENGTH && !searchLoading"
                class="block truncate text-gray-500 cursor-default"
              >
                Type at least {{ MIN_SEARCH_LENGTH }} characters to start searching.
              </span>

              <div
                v-if="!searchResults.length && searchQuery.length >= MIN_SEARCH_LENGTH && !searchLoading"
                class="flex items-center gap-2 cursor-default"
              >
                <Image :src="funnyLizardSrc" image-class="w-6 h-6" />
                <span class="block truncate text-gray-500">No results found.</span>
              </div>

              <div v-if="searchLoading" class="w-full flex justify-center">
                <ProgressSpinner class="w-10 h-10" />
              </div>

              <div
                v-if="searchResults.length && !searchLoading"
                class="w-full max-w-full max-h-[80vh] overflow-x-hidden overflow-y-auto flex flex-col gap-2"
              >
                <EqoRow v-if="selectedFilter === 'eqo'" :eqo-list="searchResults" />
                <CustomerRow v-if="selectedFilter === 'customers'" :customers="searchResults" />
                <SupplierRow v-if="selectedFilter === 'suppliers'" :suppliers="searchResults" />
                <EmployeeRow v-if="selectedFilter === 'employees'" :employees="searchResults" />
              </div>
            </div>
          </Popover>
        </div>

        <!-- User Data -->
        <div class="flex-1 flex items-center justify-end space-x-4 mr-2 md:mr-8">
          <div class="flex items-center mr-4">
            <!-- 2FA Reminder -->
            <div v-if="show2faReminder" class="flex items-center">
              <Button
                icon="pi pi-shield"
                severity="warn"
                variant="text"
                rounded
                class="relative"
                v-tooltip.bottom="'Enable 2FA to secure your account'"
                @click="goToSecurity"
              />
              <span class="absolute -mt-4 ml-6 inline-flex h-2 w-2 rounded-full bg-orange-500 animate-pulse"></span>
            </div>

            <UserReminderCalendar
              global
              :incoming-reminders="dueReminders"
            />

            <OverlayBadge
              :value="unreadNotificationsCount"
              severity="warn"
              size="small"
              :pt:pcBadge:root="[{ hidden: unreadNotificationsCount < 1 }, 'min-w-4 h-4']"
            >
              <Button
                icon="pi pi-bell text-xl"
                text
                rounded
                severity="secondary"
                class="p-0 w-7 h-7"
                @click="toggleNotificationsMenu"
              />
            </OverlayBadge>
            <Menu
              :model="notifications"
              popup
              ref="notificationsMenu"
              append-to="body"
              class="w-80 max-h-80 overflow-y-scroll shadow-xl"
              :pt="{
                root: { style: 'overflow-anchor: none' },
                list: { style: 'overflow-anchor: none' },
              }"
            >
              <template #start>
                <div v-if="unreadNotificationsCount > 0" class="border-b border-gray-200 dark:border-gray-700">
                  <Button
                    label="Mark all as read"
                    icon="pi pi-check-square"
                    severity="secondary"
                    size="small"
                    variant="text"
                    class="w-full"
                    @click="markAllAsRead"
                  />
                </div>
              </template>
              <template #item="{ item, props }">
                <StockImportedNotification
                  v-if="item.type === 'stockImported'"
                  :item="item"
                  :props="props"
                  :handleNotificationClick="handleNotificationClick"
                />
                <DeviceLoginNotification
                  v-else-if="item.type === 'deviceLoginRequested'"
                  :item="item"
                  :props="props"
                  :handleNotificationClick="handleNotificationClick"
                />
                <AnnouncementNotification
                  v-else-if="item.type === 'announcement'"
                  :item="item"
                  :props="props"
                  :handleNotificationClick="handleNotificationClick"
                />
                <MentionNotification
                  v-else
                  :item="item"
                  :props="props"
                  :handleNotificationClick="handleNotificationClick"
                />
              </template>
            </Menu>
          </div>
          <div class="hidden md:flex flex-col text-sm text-right text-gray-700 dark:text-gray-400 font-bold">
            <span>{{ authStore.user?.name?.split(' ')[0] ?? '' }}</span>
            <span>{{ authStore.user?.name?.split(' ')[1] ?? '' }}</span>
          </div>
          <div class="flex items-center justify-center">
            <UserAvatar
              :name="authStore.user?.name"
              :imagePath="authStore.user?.imagePath"
              avatarClass="mr-2 cursor-pointer w-12 h-12"
              @click="toggleUserMenu"
            />
            <Menu ref="userMenu" id="user-menu" :model="userMenuItems" :popup="true" append-to="body" />
          </div>
        </div>
      </header>

      <div
        v-if="authStore.isImpersonating"
        class="flex items-center justify-between gap-3 px-4 py-3 bg-amber-100 dark:bg-amber-900/40 border-b border-amber-300 dark:border-amber-700"
      >
        <div class="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200">
          <i class="pi pi-exclamation-triangle"></i>
          <span>
            You are impersonating <strong>{{ authStore.user?.name }}</strong>
            <template v-if="authStore.impersonatorUser">
              as <strong>{{ authStore.impersonatorUser?.name }}</strong>
            </template>
          </span>
        </div>

        <Button
          label="Stop impersonation"
          icon="pi pi-times"
          severity="warn"
          size="small"
          @click="handleStopImpersonation"
        />
      </div>

      <!-- Content -->
      <main
        class="flex-1 py-6 md:p-5 bg-gray-100 dark:bg-gray-700 max-w-[100vw]"
        :class="{
          'md:max-w-[calc(100vw-16rem)]': !isSideBarShrink,
          'md:max-w-[calc(100vw-5rem)]': isSideBarShrink,
        }"
      >
        <router-view />
        <Toast />
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { IconField, Image, InputIcon, Menu, OverlayBadge, Popover, ProgressSpinner, Toast } from 'primevue'
import SideBar from '@/components/admin/SideBar.vue'
import UserAvatar from '@/components/admin/shared/UserAvatar.vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useDeviceStore } from '@/stores/deviceStore'
import { useDarkModeStore } from '@/stores/darkModeStore'
import apiClient from '@/utils/apiClient'
import MentionNotification from '@/components/admin/shared/notifications/MentionNotification.vue'
import StockImportedNotification from '@/components/admin/shared/notifications/StockImportedNotification.vue'
import DeviceLoginNotification from '@/components/admin/shared/notifications/DeviceLoginNotification.vue'
import AnnouncementNotification from '@/components/admin/shared/notifications/AnnouncementNotification.vue'
import { useSidebarStore } from '@/stores/sideBarStore'
import { useToast } from 'primevue/usetoast'
import { debounce } from 'lodash'
import CustomerRow from '@/components/admin/search/CustomerRow.vue'
import EqoRow from '@/components/admin/search/EqoRow.vue'
import SupplierRow from '@/components/admin/search/SupplierRow.vue'
import EmployeeRow from '@/components/admin/search/EmployeeRow.vue'
import UserReminderCalendar from '@/components/admin/shared/blocks/timelines/UserReminderCalendar.vue'
import {
  initPusher,
  onMentionReceived,
  onMentionsCleared,
  onStockFileImported,
  onRemindersDue,
  onDeviceLoginRequested,
  onDeviceRevoked,
  onAnnouncementCreated,
} from '@/utils/socketService'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const deviceStore = useDeviceStore()
const darkModeStore = useDarkModeStore()
const sidebarStore = useSidebarStore()
const toast = useToast()

const MIN_SEARCH_LENGTH = 3
const searchPanel = ref()
const searchQuery = ref('')
const searchLoading = ref(false)
const searchResults = ref([])
const dueReminders = ref([])

const searchFilters = [
  { key: 'eqo', label: 'EQO', permission: 'search eqo' },
  { key: 'customers', label: 'Customers', permission: 'search customers' },
  { key: 'suppliers', label: 'Suppliers', permission: 'search suppliers' },
  { key: 'stocks', label: 'Stocks', permission: 'search stocks' },
  { key: 'warehouse', label: 'Warehouse', permission: 'search warehouse' },
  { key: 'employees', label: 'Employees', permission: 'search employees' },
]

const allowedSearchFilters = computed(() => searchFilters.filter((f) => authStore.can(f.permission)))

const selectedFilter = ref(null)
const funnyLizardSrc = new URL('@/assets/images/funny-lizard.png', import.meta.url).href

let ignoreNextScroll = false

const selectFilter = (key) => {
  if (selectedFilter.value === key) return

  selectedFilter.value = key
  searchResults.value = []
  debouncedSearch()
}

const openSearchDropdown = (event) => {
  ignoreNextScroll = true
  searchPanel.value?.show(event)
}

const closeSearchDropdown = () => {
  searchResults.value = []
  searchQuery.value = ''
  searchPanel.value?.hide()
}

const isSideBarShrink = computed({
  get: () => sidebarStore.isShrinked,
  set: (val) => sidebarStore.setShrinked(val),
})
const isSidebarVisible = ref(!deviceStore.isMobile)

const notifications = ref([])
const notificationsMenu = ref()
const unreadNotificationsCount = computed(() => notifications.value.filter((n) => !n.readAt).length)

const userMenu = ref()
const logout = async () => {
  try {
    await apiClient.post('/logout')
  } catch (error) {
    console.error('Logout failed', error)
  }

  authStore.clearAuthData()
  await router.push({ name: 'LoginPage' })
}

const userMenuItems = computed(() => {
  const accountItems = [
    {
      label: 'Profile',
      icon: 'pi pi-user text-green-500',
      command: () => {
        router.push({ name: 'UserPage', params: { id: authStore.user?.id } })
      },
    },
  ]

  if (authStore.isImpersonating) {
    accountItems.push({
      label: 'Stop impersonation',
      icon: 'pi pi-replay text-orange-500',
      command: handleStopImpersonation,
    })
  }

  accountItems.push({
    label: 'Logout',
    icon: 'pi pi-sign-out text-red-500',
    command: logout,
  })

  return [
    {
      label: authStore.user?.name || 'User',
      items: accountItems,
    },
    {
      label: 'Theme',
      items: [
        {
          label: darkModeStore.isDark ? 'Light Mode' : 'Dark Mode',
          icon: darkModeStore.isDark ? 'pi pi-sun text-yellow-400' : 'pi pi-moon text-blue-800',
          command: () => {
            darkModeStore.toggleDarkMode()
          },
        },
      ],
    },
  ]
})

const toggleUserMenu = (event) => {
  ignoreNextScroll = true
  userMenu.value.toggle(event)
}

const toggleSidebar = () => {
  if (!deviceStore.isMobile) return
  isSidebarVisible.value = !isSidebarVisible.value
}

const show2faReminder = computed(() => {
  return !!authStore.user?.id && authStore.user?.twoFactorEnabled === false
})

const goToSecurity = () => {
  router.push({ name: 'UserSecurityPage' })
}

const toggleNotificationsMenu = (event) => {
  if (notifications.value.length < 1) return
  ignoreNextScroll = true
  notificationsMenu.value.toggle(event)
}

const fetchNotifications = async () => {
  try {
    const response = await apiClient.get('/users/notifications')
    notifications.value = response.data.data
  } catch (error) {
    console.error('Failed to fetch notifications', error)
  }
}

const handleNotificationClick = async (item) => {
  try {
    if (!item.readAt) {
      await apiClient.patch(`/users/notifications/${item.id}/read`)
      const index = notifications.value.findIndex((n) => n.id === item.id)
      if (index !== -1) {
        notifications.value[index].readAt = new Date().toISOString()
      }
    }
    if (item.type === 'stockImported') {
      await navigateToStockList(item)
    } else if (item.type === 'deviceLoginRequested') {
      await router.push({ name: 'DeviceLoginsPage' })
    } else if (item.type === 'announcement') {
      await router.push({ name: 'WelcomePage' })
    } else {
      await navigateToComment(item)
    }
  } catch (error) {
    console.error('Failed to mark notification as read', error)
  }
}

const markAllAsRead = async () => {
  try {
    await apiClient.patch('/users/notifications/read-all')
    notifications.value = notifications.value.map((n) => ({
      ...n,
      readAt: n.readAt ?? new Date().toISOString(),
    }))
  } catch (error) {
    console.error('Failed to mark all notifications as read', error)
  }
}

const navigateToComment = async (item) => {
  const targetPath = new URL(item.page).pathname
  const samePage = route.path === targetPath
  const isEqoPage = targetPath.includes('/eqo/')

  const statePayload = {
    ...(window.history.state || {}),
    openComment: {
      id: item.commentId,
      type: item.isComment ? 'comment' : 'reply',
      ts: Date.now(),
    },
  }

  if (samePage) {
    window.history.replaceState(statePayload, document.title)
    window.dispatchEvent(new CustomEvent('open-comment', { detail: statePayload.openComment }))
    return
  }

  await router.push({
    path: targetPath,
    query: isEqoPage ? { tab: 'comments' } : undefined,
    state: statePayload,
  })
}

const navigateToStockList = async (item) => {
  if (item.stockFile.supplierId) {
    await router.push({
      name: 'SupplierUploadStockListPage',
      params: { supplierId: item.stockFile.supplierId },
    })
    return
  }

  await router.push({ name: 'WebsiteUploadStockListPage' })
}

const search = async () => {
  const q = searchQuery.value?.trim() ?? ''

  if (!selectedFilter.value) {
    searchResults.value = []
    return
  }

  const allowedKeys = allowedSearchFilters.value.map((f) => f.key)
  if (!allowedKeys.includes(selectedFilter.value)) {
    searchResults.value = []
    return
  }

  if (q.length < MIN_SEARCH_LENGTH) {
    searchResults.value = []
    return
  }

  if (!q) {
    searchResults.value = []
    return
  }

  searchLoading.value = true

  try {
    const response = await apiClient.get('/search', {
      params: { q, scope: selectedFilter.value },
    })

    searchResults.value = response.data.data
  } catch (error) {
    console.error(error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'An error occurred while searching.',
      life: 3000,
    })
  } finally {
    searchLoading.value = false
  }
}

const debouncedSearch = debounce(search, 500)

const handleStopImpersonation = async () => {
  try {
    await authStore.stopImpersonation()

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Impersonation stopped successfully.',
      life: 3000,
    })

    await router.push({ name: 'UsersPage' })
  } catch (error) {
    console.error('Failed to stop impersonation', error)

    authStore.clearAuthData()
    await router.push({ name: 'LoginPage' })

    toast.add({
      severity: 'error',
      summary: 'Session expired',
      detail: error.response?.data?.message || 'Failed to stop impersonation.',
      life: 3000,
    })
  }
}

const closeAllOverlays = () => {
  if (ignoreNextScroll) {
    ignoreNextScroll = false
    return
  }
  searchPanel.value?.hide()
  notificationsMenu.value?.hide()
  userMenu.value?.hide()
}

watch(
  allowedSearchFilters,
  (filters) => {
    if (!filters.length) {
      selectedFilter.value = null
      return
    }

    const stillAllowed = filters.some((f) => f.key === selectedFilter.value)
    if (!stillAllowed) {
      selectedFilter.value = filters[0].key
    }
  },
  { immediate: true }
)

onMounted(() => {
  darkModeStore.applyTheme()
  fetchNotifications()

  initPusher()
  onMentionReceived((data) => {
    notifications.value.unshift(data)
  })
  onMentionsCleared(({ commentId }) => {
    notifications.value = notifications.value.filter((n) => n.commentId !== commentId)
  })
  onStockFileImported((data) => {
    notifications.value.unshift(data)
  })
  onRemindersDue((data) => {
    dueReminders.value = data.reminders ?? []
  })
  onDeviceLoginRequested((data) => {
    notifications.value.unshift(data)
  })
  onDeviceRevoked(({ deviceId }) => {
    if (deviceId === localStorage.getItem('deviceId')) {
      authStore.clearAuthData()
      router.replace({ name: 'LoginPage' })
    }
  })
  onAnnouncementCreated((data) => {
    notifications.value.unshift(data)
  })
  window.addEventListener('scroll', closeAllOverlays, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', closeAllOverlays)
})
</script>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-in-out;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
